import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useTaskCost } from '../hooks/useTaskCost';
import { useQueryClient } from '@tanstack/react-query';
import { getApiUrl } from '../utils/config';
import { ImageToVideoForm, ImageToVideoData } from '../components/ai/ImageToVideoForm';
import mediaService from '../services/mediaService';
import AuthenticatedImage from '../components/media/AuthenticatedImage';
import { useNavigate } from 'react-router-dom';
import { 
  Download, 
  Save, 
  Loader2, 
  Wand2, 
  X, 
  Film, 
  ArrowRight, 
  HelpCircle, 
  Eye, 
  Share2,
  Sparkles,
  Clock,
  CheckCircle,
  AlertCircle,
  Target,
  Upload,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { motion, AnimatePresence } from 'framer-motion';
import ComingSoonWrapper from '../components/launch/ComingSoonWrapper';

const AdStudioVideoPage: React.FC = () => {
  const { currentClient } = useClient();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Task costs for video generation (credit-based only)
  const videoTask = useTaskCost('Image to Video Generation');
  const [generatedMediaId, setGeneratedMediaId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [originalInputMediaId, setOriginalInputMediaId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'configure' | 'generate' | 'complete'>('upload');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);
  const [formData, setFormData] = useState<ImageToVideoData>({
    input_media_file_id: '',
    prompt: '',
    duration: 5,
    motion_type: 'subtle',
    resolution: '1024x576',
  });

  useEffect(() => {
    return () => {
      if (generatedVideoUrl && generatedVideoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(generatedVideoUrl);
      }
    };
  }, [generatedVideoUrl]);

  // Update step based on form data
  useEffect(() => {
    if (formData.input_media_file_id && currentStep === 'upload') {
      setCurrentStep('configure');
    } else if (!formData.input_media_file_id && currentStep !== 'upload') {
      setCurrentStep('upload');
    }
  }, [formData.input_media_file_id, currentStep]);

  // Calculate estimated processing time
  const getEstimatedTime = useCallback((duration: number, motionType: string, resolution: string) => {
    const baseTime = 60; // Base 60 seconds
    const durationMultiplier = duration / 5; // Scale based on duration
    const motionMultiplier = {
      'subtle': 1,
      'moderate': 1.2,
      'dynamic': 1.5,
      'cinematic': 1.8
    }[motionType] || 1;
    const resolutionMultiplier = resolution === '1280x720' ? 1.3 : 1;
    
    return Math.ceil(baseTime * durationMultiplier * motionMultiplier * resolutionMultiplier);
  }, []);

  const estimatedTime = getEstimatedTime(
    formData.duration || 5,
    formData.motion_type || 'subtle',
    formData.resolution || '1024x576'
  );

  const pollTaskStatus = (taskId: string) => {
    let pollCount = 0;
    const maxPolls = Math.ceil(estimatedTime / 3); // Poll every 3 seconds
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'X-Client-ID': currentClient?.id || '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to check task status');
        }

        const task = await response.json();
        console.log('Task status:', task.status);

        // Update progress based on polling
        pollCount++;
        const progress = Math.min((pollCount / maxPolls) * 90, 90); // Max 90% until complete
        setProcessingProgress(progress);
        
        const timeRemaining = Math.max(estimatedTime - (pollCount * 3), 0);
        setEstimatedTimeRemaining(timeRemaining);

        if (task.status === 'completed') {
          // Task completed successfully
          setProcessingProgress(100);
          setEstimatedTimeRemaining(0);
          setCurrentStep('complete');
          
          if (task.output_media_file_ids && task.output_media_file_ids.length > 0 && currentClient) {
            console.log('Getting media file:', task.output_media_file_ids[0]);
            try {
              const mediaFile = await mediaService.getMediaFile(task.output_media_file_ids[0], currentClient.id);
              console.log('Media file retrieved:', mediaFile);
              
              const videoUrl = mediaFile.file_url || mediaFile.thumbnail_url;
              console.log('Setting video URL:', videoUrl);
              
              // Ensure we have a full URL for the video element
              const fullVideoUrl = videoUrl?.startsWith('http') ? videoUrl : `${getApiUrl()}${videoUrl}`;
              console.log('Full video URL:', fullVideoUrl);
              
              setGeneratedVideoUrl(fullVideoUrl);
              setGeneratedMediaId(task.output_media_file_ids[0]);
            } catch (error) {
              console.error('Error getting media file:', error);
              toast({
                title: "Error Loading Video",
                description: "The video was generated but couldn't be loaded.",
                variant: "destructive",
              });
            }
          }
          
          setIsLoading(false);
          toast({
            title: "🎬 Video Generation Complete!",
            description: "Your cinematic masterpiece is ready to captivate your audience!",
          });
        } else if (task.status === 'failed') {
          // Task failed
          setIsLoading(false);
          setCurrentStep('configure');
          setProcessingProgress(0);
          setEstimatedTimeRemaining(null);
          toast({
            title: "Video Generation Failed",
            description: task.error_message || "The video generation failed. Please try again.",
            variant: "destructive",
          });
        } else if (task.status === 'processing' || task.status === 'pending') {
          // Still processing, continue polling
          setTimeout(checkStatus, 3000); // Poll every 3 seconds
        }
      } catch (error) {
        console.error('Error checking task status:', error);
        setIsLoading(false);
        setCurrentStep('configure');
        setProcessingProgress(0);
        setEstimatedTimeRemaining(null);
        toast({
          title: "Connection Error",
          description: "Failed to check video generation status. Please try again.",
          variant: "destructive",
        });
      }
    };

    // Start polling
    setTimeout(checkStatus, 2000); // Initial delay of 2 seconds
  };

  const handleTaskCreated = async (data: ImageToVideoData) => {
    setOriginalInputMediaId(data.input_media_file_id);
    
    // Check if limit is reached
    if (videoTask.isLimitReached) {
      toast({
        title: "Limit Reached",
        description: `You've reached your monthly video generation limit (${videoTask.usage}/${videoTask.limit}). Upgrade your plan for more generations.`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      setGeneratedVideoUrl(null);
      setCurrentStep('generate');
      setProcessingProgress(0);
      setEstimatedTimeRemaining(estimatedTime);

      if (!data.input_media_file_id || !currentClient) {
        toast({ title: "Missing Image", description: "Please select an image to generate a video.", variant: "destructive" });
        setIsLoading(false);
        setCurrentStep('configure');
        return;
      }

      // Get input media file for preview
      try {
        const inputMediaFile = await mediaService.getMediaFile(data.input_media_file_id, currentClient.id);
        setSelectedImage(inputMediaFile.thumbnail_url || inputMediaFile.file_url || null);
      } catch (error) {
        console.error('Failed to fetch input media file:', error);
        toast({ title: "Image Error", description: "Failed to fetch input image for video generation.", variant: "destructive" });
        setIsLoading(false);
        setCurrentStep('configure');
        return;
      }

      // Call the real image-to-video API
      const response = await fetch(`${getApiUrl()}/api/v1/ai/image-to-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({
          input_media_file_id: data.input_media_file_id,
          prompt: data.prompt || '',
          duration: data.duration || 5,
          motion_type: data.motion_type || 'subtle',
          resolution: data.resolution || '1024x576'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create video generation task');
      }

      const result = await response.json();
      console.log('Video generation task created:', result);

      // Refresh user data to update credits and limits
      try {
        await refreshUser();
        queryClient.invalidateQueries({ queryKey: ['feature-limit'] });
        queryClient.invalidateQueries({ queryKey: ['taskCost'] });
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }

      toast({
        title: "🎬 Video Generation Started",
        description: `Your AI is crafting a ${data.duration}s video with ${data.motion_type} motion. Estimated time: ${Math.ceil(estimatedTime / 60)}m`,
      });

      // Start polling for task completion
      pollTaskStatus(result.task.id);

    } catch (error: any) {
      console.error('Video generation error:', error);
      
      // Handle specific error cases
      if (error.message.includes('Insufficient credits')) {
        toast({ 
          title: "Insufficient Credits", 
          description: "You don't have enough credits to generate a video. Please purchase more credits.", 
          variant: "destructive" 
        });
      } else if (error.message.includes('currently disabled')) {
        toast({ 
          title: "Feature Unavailable", 
          description: "Video generation is currently disabled. Please try again later.", 
          variant: "destructive" 
        });
      } else {
        toast({ 
          title: "Generation Failed", 
          description: error.message || "Failed to start video generation. Please try again.", 
          variant: "destructive" 
        });
      }
      
      setIsLoading(false);
      setCurrentStep('configure');
      setProcessingProgress(0);
      setEstimatedTimeRemaining(null);
    }
  };

  const handleDownload = async () => {
    if (!generatedVideoUrl || !currentClient) return;
    
    try {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'X-Client-ID': currentClient.id,
      };

      const isAbsoluteUrl = generatedVideoUrl.startsWith('http');
      const fetchOptions = isAbsoluteUrl ? {} : { headers };

      const response = await fetch(generatedVideoUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-video-${Date.now()}.mp4`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({ 
        title: "📥 Download Complete", 
        description: "Your video has been saved to your device!" 
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({ 
        title: "Download Failed", 
        description: error instanceof Error ? error.message : "Please try again", 
        variant: "destructive" 
      });
    }
  };

  const handleCreatePost = async () => {
    if (!generatedVideoUrl || !currentClient || !generatedMediaId) return;
    
    try {
      toast({ title: "📝 Creating Post", description: "Adding your video to the post editor..." });
      
      // Navigate to post creation with the generated media ID
      navigate('/app/posts/new', { 
        state: { 
          selectedMediaIds: [generatedMediaId],
          generatedFromAdStudio: true 
        } 
      });
      
      toast({ 
        title: "🚀 Ready to Post", 
        description: "Your video has been added to the post editor. Time to share it with the world!" 
      });
    } catch (error) {
      console.error('Failed to create post:', error);
      toast({ 
        title: "Failed to Create Post", 
        description: error instanceof Error ? error.message : "Please try again", 
        variant: "destructive" 
      });
    }
  };

  const resetFlow = () => {
    setGeneratedVideoUrl(null);
    setGeneratedMediaId(null);
    setSelectedImage(null);
    setOriginalInputMediaId(null);
    setCurrentStep('upload');
    setProcessingProgress(0);
    setEstimatedTimeRemaining(null);
    setFormData({
      input_media_file_id: '',
      prompt: '',
      duration: 5,
      motion_type: 'subtle',
      resolution: '1024x576',
    });
  };

  if (!currentClient) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-900 text-white">
        <p className="text-2xl font-bold text-red-400 mb-4">No Client Selected</p>
        <p className="text-slate-400">Please select a client to access the Ad Studio.</p>
      </div>
    );
  }

  return (
    <ComingSoonWrapper
      feature="video_generation"
      title="Video Studio Coming Soon"
      description=" We are working hard to get Video Studio ready. You will be able to transform your static images into dynamic, engaging videos with AI-powered motion and effects."
      features={[
        "AI-powered image-to-video generation",
        "High-quality output resolutions",
        "Professional video effects and transitions",
        "Direct integration with post creation"
      ]}

      icon="zap"
    >
      <TooltipProvider>
        <div className="min-h-screen bg-background text-foreground">
        {/* Floating Header */}
        <div className="backdrop-blur-2xl bg-background/60 border-b border-border/30">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-primary/25">
                    <Film className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Video Studio
                  </h1>
                  <p className="hidden sm:block text-muted-foreground">Transform images into cinematic videos</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="group relative overflow-hidden bg-gradient-to-br from-pink-500/10 to-purple-500/10 hover:from-pink-500/20 hover:to-purple-500/20 border border-pink-500/20 hover:border-pink-500/30 text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 transition-all duration-200 rounded-lg backdrop-blur-sm"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  <HelpCircle className="w-4 h-4 mr-1 sm:mr-2 relative z-10" />
                  <span className="hidden sm:inline relative z-10 font-medium">Tour</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Panel - Controls */}
            <div className="lg:col-span-5 space-y-3">
              

              
              {/* Compact Progress Steps */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-primary/5"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
                <div className="relative p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Progress</h3>
                    <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
                      {['upload', 'configure', 'generate', 'complete'].indexOf(currentStep) + 1}/4
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {[
                      { key: 'upload', icon: Upload, label: 'Upload', color: 'blue' },
                      { key: 'configure', icon: Wand2, label: 'Configure', color: 'purple' },
                      { key: 'generate', icon: Film, label: 'Generate', color: 'pink' },
                      { key: 'complete', icon: CheckCircle, label: 'Complete', color: 'green' }
                    ].map((step, index) => {
                      const stepIndex = ['upload', 'configure', 'generate', 'complete'].indexOf(currentStep);
                      const isActive = currentStep === step.key;
                      const isCompleted = index < stepIndex;
                      const colorClasses = {
                        blue: 'from-blue-500 to-blue-600',
                        purple: 'from-purple-500 to-purple-600',
                        pink: 'from-pink-500 to-pink-600',
                        green: 'from-green-500 to-green-600'
                      };
                      
                      return (
                        <div key={step.key} className="flex flex-col items-center space-y-1 relative">
                          <motion.div 
                            className={`relative flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-500 ${
                              isActive 
                                ? `bg-gradient-to-br ${colorClasses[step.color]} text-white shadow-lg shadow-${step.color}-500/30` 
                                : isCompleted
                                  ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30'
                                  : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-600'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <step.icon className="w-3.5 h-3.5" />
                            {isActive && (
                              <motion.div
                                className="absolute inset-0 rounded-lg border border-white/50"
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              />
                            )}
                          </motion.div>
                          <span className={`text-xs font-medium transition-colors ${
                            isActive ? `text-${step.color}-600 dark:text-${step.color}-400` : 
                            isCompleted ? 'text-green-600 dark:text-green-400' :
                            'text-slate-500 dark:text-slate-400'
                          }`}>
                            {step.label}
                          </span>

                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>

              <form onSubmit={(e) => { e.preventDefault(); handleTaskCreated(formData); }} className="space-y-3">
                
                {/* Video Generation Form */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-purple-500/5"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
                  <div className="relative p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                        <Film className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">Image to Video</h3>
                        <p className="text-xs text-slate-600 dark:text-slate-400">Transform static images into dynamic videos</p>
                      </div>
                    </div>

                    <ImageToVideoForm
                      onSubmit={handleTaskCreated}
                      isLoading={isLoading}
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </div>
                </motion.div>

                {/* Generate Video Button */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="relative"
                >
                      <div className="w-full">
                        <Button
                          type="submit"
                          disabled={isLoading || !formData.input_media_file_id || videoTask.isLimitReached}
                          className={`relative w-full py-4 rounded-xl font-bold text-base shadow-2xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:transform-none overflow-hidden group ${
                            videoTask.isLimitReached 
                              ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' 
                              : 'bg-gradient-to-r from-blue-500 via-purple-600 to-pink-600 hover:from-blue-400 hover:via-purple-500 hover:to-pink-500 text-white hover:shadow-blue-500/25'
                          }`}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          {isLoading ? (
                            <div className="flex items-center justify-center relative z-10">
                              <motion.div
                                className="w-5 h-5 mr-2"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              >
                                <Film className="w-5 h-5 text-white" />
                              </motion.div>
                              <span>Generating Video...</span>
                            </div>
                          ) : videoTask.isLimitReached ? (
                            <div className="flex items-center justify-center relative z-10">
                              <AlertCircle className="mr-2 h-5 w-5" />
                              <span>Limit Reached</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between w-full px-3 relative z-10">
                              <div className="flex items-center">
                                <Film className="mr-2 h-5 w-5" />
                                <span>Generate Video</span>
                              </div>
                              <div className="flex items-center space-x-1 bg-white/20 px-2 py-1 rounded-full">
                                <span className="text-sm font-bold">{videoTask.cost}</span>
                                <span className="text-xs">credits</span>
                              </div>
                            </div>
                          )}
                        </Button>
                      </div>
                </motion.div>
              </form>
            </div>

            {/* Right Panel - Preview */}
            <div className="lg:col-span-7 space-y-4">
              
              {/* Video Preview Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border-border/30 shadow-lg shadow-slate-500/5"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>

                {/* Video Header */}
                <div className="relative border-b border-slate-200/50 dark:border-slate-700/50 p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Film className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Video Preview</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Your AI-generated video will appear here</p>
                      </div>
                    </div>
                    {generatedVideoUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetFlow}
                        className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                      </Button>
                    )}
                  </div>
                </div>

                {/* Video Content */}
                <div className="relative p-4 flex items-center justify-center min-h-[400px]">
                  <AnimatePresence mode="wait">
                    {isLoading ? (
                      <motion.div
                        key="loading"
                        className="text-center space-y-6 w-full"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.5 }}
                      >
                        {/* Enhanced Video Generation Animation */}
                        <div className="relative">
                          {/* Central pulsing element */}
                          <motion.div
                            className="w-32 h-32 mx-auto relative"
                            animate={{
                              scale: [1, 1.05, 1],
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            {/* Outer ring with progress */}
                            <motion.div
                              className="absolute inset-0 rounded-full"
                              style={{
                                background: `conic-gradient(from 0deg, #3b82f6 ${processingProgress}%, #e2e8f0 ${processingProgress}%)`,
                                borderRadius: '50%',
                                padding: '4px'
                              }}
                              animate={{ rotate: 360 }}
                              transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "linear"
                              }}
                            >
                              <div className="w-full h-full bg-white dark:bg-slate-800 rounded-full"></div>
                            </motion.div>
                            
                            {/* Inner rotating elements */}
                            {[...Array(2)].map((_, i) => (
                              <motion.div
                                key={i}
                                className="absolute inset-4 rounded-full border-2 border-blue-500/20"
                                animate={{ rotate: -360 }}
                                transition={{
                                  duration: 3 + i * 0.5,
                                  repeat: Infinity,
                                  ease: "linear",
                                  delay: i * 0.3
                                }}
                                style={{
                                  borderStyle: 'dashed',
                                }}
                              />
                            ))}
                            
                            {/* Center icon */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <motion.div
                                animate={{
                                  scale: [1, 1.1, 1],
                                  rotate: [0, 3, -3, 0]
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              >
                                <Film className="w-12 h-12 text-blue-500" />
                              </motion.div>
                            </div>
                            
                            {/* Progress percentage */}
                            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                              <div className="bg-blue-500/10 px-3 py-1 rounded-full">
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                  {Math.round(processingProgress)}%
                                </span>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                        
                        <motion.div 
                          className="space-y-4"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3, duration: 0.6 }}
                        >
                          <h4 className="text-2xl font-bold text-slate-900 dark:text-white">Generating Video</h4>
                          <p className="text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                            AI is crafting your {formData.duration}s video with {formData.motion_type} motion...
                          </p>
                          
                          {/* Time remaining */}
                          {estimatedTimeRemaining !== null && (
                            <div className="flex items-center justify-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
                              <Clock className="w-4 h-4" />
                              <span>
                                {estimatedTimeRemaining > 60 
                                  ? `${Math.ceil(estimatedTimeRemaining / 60)}m remaining`
                                  : `${estimatedTimeRemaining}s remaining`
                                }
                              </span>
                            </div>
                          )}
                          
                          {/* Processing steps */}
                          <div className="mt-6 space-y-2">
                            {[
                              "Analyzing source image structure...",
                              "Generating motion vectors...", 
                              "Rendering video frames...",
                              "Applying final effects..."
                            ].map((step, i) => (
                              <motion.div
                                key={i}
                                className="text-sm text-slate-500 dark:text-slate-400"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ 
                                  opacity: processingProgress > (i * 25) ? [0.7, 1, 0.7] : 0.3,
                                  x: 0
                                }}
                                transition={{
                                  delay: i * 0.5,
                                  duration: 2,
                                  repeat: processingProgress > (i * 25) ? Infinity : 0,
                                  repeatDelay: 2
                                }}
                              >
                                <div className="flex items-center space-x-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    processingProgress > (i * 25) ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600'
                                  }`} />
                                  <span>{step}</span>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      </motion.div>
                    ) : generatedVideoUrl ? (
                      <motion.div
                        key="video"
                        className="w-full"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.6 }}
                      >
                        <div className="relative rounded-xl border border-slate-300 dark:border-slate-600 p-3 bg-slate-50 dark:bg-slate-800">
                          <div className="w-full aspect-video flex items-center justify-center bg-black rounded-lg overflow-hidden">
                            <video
                              src={generatedVideoUrl}
                              controls
                              autoPlay
                              loop
                              className="w-full h-full object-contain"
                            />
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center justify-between mt-6">
                          <div className="flex items-center space-x-3">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={handleDownload}
                              className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={handleCreatePost}
                              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Create Post
                            </Button>
                          </div>
                          
                          {/* Video Info */}
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {formData.duration}s • {formData.resolution}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                              {formData.motion_type} motion
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="placeholder"
                        className="text-center space-y-6 w-full"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.6 }}
                      >
                        {/* Minimalist Video Placeholder */}
                        <div className="relative mx-auto w-full max-w-lg">
                          <div className="aspect-video relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 border-2 border-dashed border-slate-300 dark:border-slate-600 shadow-lg">
                            {/* Central content */}
                            <div className="relative z-10 flex items-center justify-center h-full">
                              <div className="text-center space-y-4">
                                {/* Simple film icon */}
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 rounded-2xl flex items-center justify-center mx-auto">
                                  <Film className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                                </div>
                                
                                <div className="space-y-2">
                                  <h4 className="text-lg font-semibold text-slate-600 dark:text-slate-300">Ready to Create</h4>
                                  <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                                    Your AI-generated video will appear here
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Compact Instructions */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-center space-x-4 text-sm text-slate-600 dark:text-slate-400">
                            <div className="flex items-center space-x-2">
                              <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg">1</div>
                              <span className="font-medium">Upload</span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <div className="flex items-center space-x-2">
                              <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg">2</div>
                              <span className="font-medium">Configure</span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <div className="flex items-center space-x-2">
                              <div className="w-7 h-7 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg">3</div>
                              <span className="font-medium">Generate</span>
                            </div>
                          </div>
                          

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
    </ComingSoonWrapper>
  );
};

export default AdStudioVideoPage;